import Search from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import { debounce, get, startCase, toLower } from "lodash";
import isEmpty from "lodash/isEmpty";
import AutocompleteFreeform from "../../../common/js/components/AutocompleteFreeform";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import searchConstants from "../search/constants";
import { us_states_options } from "../utils/constants";
import { representation_type_options } from "./constants";

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const StyledModalPaper = styled("div")(({ theme }) => ({
    position: "absolute",
    width: theme.spacing(70),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
}));

const StyledCompanyContainer = styled("div")({
    height: "99px",
    display: "flex",
    flexWrap: "wrap",
    boxSizing: "border-box",
    margin: "-4px",
    width: "100%",
});

const debounceSearchCompany = debounce((handleSearchCompany, term) => {
    handleSearchCompany(term, "REAL_ESTATE");
}, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

const OutsideSplitModal = (props) => {
    const {
        autocompleteResults,
        commissionType,
        errors,
        freeformUpdate,
        handleBrokerPersonChange,
        handleDeleteOutsideSplit,
        handleReducerLevelInput,
        handleSearchCompany,
        handleSearchOutsideBroker,
        isFreeformSearchEnabled,
        open,
        saveOutsideSplit,
        secondaryAction,
        splitId,
        toggleOutsideSplitModal,
        updateSplit,
        values,
    } = props;

    const headerText = commissionType === "REFERRAL" ? "OUTSIDE REFERRAL" : commissionType;

    const handleChange = (event) => {
        updateSplit(splitId, event.target.id, event.target.value, "outsideSplitModal");
    };

    const handleChangePreserveExistingRecord = (event, id, source) => {
        handleChange(event);
        updateSplit(splitId, id, "", source);
        updateSplit(splitId, "companyKey", "", source);
        updateSplit(splitId, "companyAddressKey", "", source);
        updateSplit(splitId, "approvedFlg", false, source);
    };

    const handleUpdateCommissionComment = (event, splitId) => {
        const commissionComment = {
            ...values.commissionComment,
            comment: event.target.value,
            commentOwnerType: "BROKER_COMMISSION",
        };
        updateSplit(splitId, "commissionComment", commissionComment, "outsideSplitModal");
    };

    const handleSaveOutsideSplit = (splitId, values) => {
        saveOutsideSplit(splitId, {
            ...values,
            commissionType: commissionType,
        });
    };

    const freeformGetCompanyAddress = (suggestion) => {
        let result = "";
        const address = get(suggestion, "mainAddress.address1", "");
        const city = get(suggestion, "mainAddress.city.city", "");
        const state = get(suggestion, "mainAddress.city.state.stateCode", "");
        const zipCode = get(suggestion, "mainAddress.zipCode.zipCode", "");

        if (address && city && state && zipCode) result = [address, city, state, zipCode].join(", ");

        return result;
    };

    const handleCompanyChange = (company, splitKey, source) => {
        updateSplit(splitKey, "companyName", get(company, "displayName"), source);
        updateSplit(splitKey, "companyAddress", get(company, "mainAddress.address1", ""), source);
        updateSplit(splitKey, "companyAddressKey", get(company, "mainAddress.key", ""), source);
        updateSplit(splitKey, "companyCityKey", get(company, "mainAddress.city.key", ""), source);
        updateSplit(splitKey, "companyCity", get(company, "mainAddress.city.city", ""), source);
        updateSplit(splitKey, "companyState", get(company, "mainAddress.city.state.stateCode", ""), source);
        updateSplit(splitKey, "companyZip", get(company, "mainAddress.zipCode.zipCode", ""), source);
        updateSplit(splitKey, "companyZipKey", get(company, "mainAddress.zipCode.key", ""), source);
        updateSplit(splitKey, "companyKey", get(company, "companyKey", ""), source);
        updateSplit(splitKey, "approvedFlg", get(company, "approvedFlg", ""), source);
        updateSplit(splitKey, "companyTypes", get(company, "companyTypes", ""), source);
        updateSplit(splitKey, "freeFormValue", get(company, "displayName", ""), source);
        handleReducerLevelInput(
            `outsideSplitModal.brokerCommissions.${splitKey}.company.companyKey`,
            get(company, "companyKey"),
            source
        );
    };

    const freeformUpdateCompany = (splitKey, value, source) => {
        if (get(values, `companyKey`, null)) {
            clearCompany();
            updateSplit(splitKey, "companyName", value, source);
        }
        updateSplit(splitKey, "companyName", value, source);
        updateSplit(splitKey, "companyTypes", ["REAL_ESTATE"], source);
        updateSplit(splitKey, "approvedFlg", false, source);
    };

    const clearCompany = () => {
        updateSplit(splitId, "companyKey", "", "outsideSplitModal");
        updateSplit(splitId, "companyName", "", "outsideSplitModal");
        updateSplit(splitId, "companyAddress", "", "outsideSplitModal");
        updateSplit(splitId, "companyAddressKey", "", "outsideSplitModal");
        updateSplit(splitId, "companyCity", "", "outsideSplitModal");
        updateSplit(splitId, "companyCityKey", "", "outsideSplitModal");
        updateSplit(splitId, "companyState", "", "outsideSplitModal");
        updateSplit(splitId, "companyZip", "", "outsideSplitModal");
        updateSplit(splitId, "companyZipKey", "", "outsideSplitModal");
        updateSplit(splitId, "freeFormValue", "", "outsideSplitModal");
    };

    const companyInputChange = (event) => {
        updateSplit(splitId, event.target.id, event.target.value, "outsideSplitModal");
        updateSplit(splitId, "companyTypes", ["INDIVIDUAL"], "outsideSplitModal");
        updateSplit(splitId, "approvedFlg", false, "outsideSplitModal");
        updateSplit(splitId, "companyKey", null, "outsideSplitModal");
    };

    return (
        <Modal
            aria-labelledby="outside-split-modal"
            aria-describedby="add-outside-split"
            open={open}
            onClose={toggleOutsideSplitModal}
        >
            <StyledModalPaper style={getModalStyle()}>
                <div
                    style={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        display: "flex",
                        flexWrap: "wrap",
                        boxSizing: "border-box",
                    }}
                >
                    <div style={{ flexGrow: 0, flexBasis: "50%", maxWidth: "50%", boxSizing: "border-box" }}>
                        <h4 className="modal-title">Allocation for {startCase(toLower(headerText))}</h4>
                    </div>
                    <div style={{ margin: 0, boxSizing: "border-box" }}>
                        {secondaryAction === "Delete" && (
                            <Button onClick={() => handleDeleteOutsideSplit(splitId)}>Delete this split</Button>
                        )}
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        boxSizing: "border-box",
                        margin: "-4px",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "33.333333%",
                            maxWidth: "33.333333%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <AutocompleteFreeform
                            id="firstName"
                            label={commissionType === "REFERRAL" ? "Outside Referrer" : "Outside Broker"}
                            error={get(errors, `firstName`, null)}
                            initialInputValue={`${get(values, `firstName`, "")}${
                                get(values, `firstName`, null) ? " " : ""
                            }${get(values, `lastName`, "")}`}
                            itemToString={(i) => get(i, `firstName`, null)}
                            onSelect={(p, b) => handleBrokerPersonChange(p, splitId, "outsideSplitModal")}
                            placeholder={"Enter name"}
                            searchFunction={handleSearchOutsideBroker}
                            startAdornment={<Search />}
                            suggestions={autocompleteResults}
                            disabled={false}
                            freeformUpdate={(value) => freeformUpdate(splitId, value, "outsideSplitModal")}
                            options={{
                                key: "brokerAgentKey",
                                display: (suggestion) => {
                                    return get(suggestion, "firstName", "") + " " + get(suggestion, "lastName", "");
                                },
                            }}
                            errorText="Required"
                        />
                    </div>
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "25%",
                            maxWidth: "25%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSSelect
                            id="representationType"
                            label="Representation"
                            value={values.representationType}
                            error={get(errors, `representationType`, null)}
                            errorText="Required"
                            onChange={handleChange}
                            placeholder="Representation"
                            options={representation_type_options}
                        />
                    </div>
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "16.666667%",
                            maxWidth: "16.666667%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSInput
                            id="netPercent"
                            value={values.netPercent}
                            error={get(errors, `netPercent`, null)}
                            errorText="Required"
                            onChange={handleChange}
                            startAdornment="%"
                        />
                    </div>
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "16.666667%",
                            maxWidth: "16.666667%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSInput
                            id="netAmount"
                            value={values.netAmount}
                            error={get(errors, `netAmount`, null)}
                            errorText="Required"
                            onChange={handleChange}
                            startAdornment="$"
                        />
                    </div>
                </div>
                <StyledCompanyContainer>
                    {/* Requirement 6.3.19 - Leave all fields editable (in case address is not populated when a company is selected)
                All outside splits must be submitted with company and address, so all fields in modal must be filled out */}

                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        {isFreeformSearchEnabled ? (
                            <AutocompleteFreeform
                                id="companyName"
                                label={"Company Name"}
                                error={get(errors, `companyName`, null)}
                                errorText="Required"
                                initialInputValue={get(values, `companyName`, "")}
                                itemToString={(i) => get(i, `companyName`, null)}
                                onSelect={(p, b) => handleCompanyChange(p, splitId, "outsideSplitModal")}
                                placeholder="Enter company"
                                searchFunction={(input) => debounceSearchCompany(handleSearchCompany, input)}
                                startAdornment={<Search />}
                                suggestions={autocompleteResults}
                                disabled={!isEmpty(get(values, `companyKey`, null))}
                                freeformUpdate={(value) => freeformUpdateCompany(splitId, value, "outsideSplitModal")}
                                options={{
                                    key: "companyKey",
                                    display: (suggestion) => {
                                        return get(suggestion, "displayName", "");
                                    },
                                    displaySub: (suggestion) => {
                                        return freeformGetCompanyAddress(suggestion);
                                    },
                                }}
                            />
                        ) : (
                            <BSInput
                                id="companyName"
                                label="Company Name"
                                value={values.companyName}
                                error={get(errors, `companyName`, null)}
                                errorText="Required"
                                onChange={companyInputChange}
                                placeholder="Enter company"
                            />
                        )}
                    </div>
                </StyledCompanyContainer>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        boxSizing: "border-box",
                        margin: "-4px",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSInput
                            id="companyAddress"
                            label="Company Address"
                            value={get(values, "companyAddress", "")}
                            error={get(errors, `companyAddress`, null)}
                            errorText="Required"
                            onChange={(event) =>
                                handleChangePreserveExistingRecord(event, "companyAddressKey", "outsideSplitModal")
                            }
                            placeholder="Enter address"
                        />
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        boxSizing: "border-box",
                        margin: "-4px",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "33.333333%",
                            maxWidth: "33.333333%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSInput
                            id="companyCity"
                            label="City"
                            value={values.companyCity}
                            error={get(errors, `companyCity`, null)}
                            errorText="Required"
                            onChange={(event) =>
                                handleChangePreserveExistingRecord(event, "companyCityKey", "outsideSplitModal")
                            }
                            placeholder="Enter city"
                        />
                    </div>

                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "33.333333%",
                            maxWidth: "33.333333%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSSelect
                            id="companyState"
                            label="State"
                            value={values.companyState}
                            error={get(errors, `companyState`, null)}
                            errorText="Required"
                            onChange={(event) =>
                                handleChangePreserveExistingRecord(event, "companyCityKey", "outsideSplitModal")
                            }
                            placeholder="Select"
                            options={us_states_options}
                        />
                    </div>

                    <div
                        style={{
                            flexGrow: 0,
                            flexBasis: "33.333333%",
                            maxWidth: "33.333333%",
                            boxSizing: "border-box",
                            padding: "4px",
                        }}
                    >
                        <BSInput
                            id="companyZip"
                            label="Zip"
                            value={values.companyZip}
                            error={get(errors, `companyZip`, null)}
                            errorText="Required (ex. 98101)"
                            onChange={(event) =>
                                handleChangePreserveExistingRecord(event, "companyZipKey", "outsideSplitModal")
                            }
                            placeholder="Enter zip code"
                        />
                    </div>
                </div>
                {commissionType === "REFERRAL" && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            boxSizing: "border-box",
                            margin: "-4px",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                flexGrow: 0,
                                flexBasis: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                padding: "4px",
                            }}
                        >
                            <BSInput
                                id="commissionComment"
                                placeholder="Enter referral explanation"
                                label="COMMENT"
                                toolTipText="Please enter an explanation for why this referral should be paid."
                                rows={5}
                                onChange={(event) => handleUpdateCommissionComment(event, splitId)}
                                error={get(errors, "commissionComment", null)}
                                value={get(values, "commissionComment.comment", null)}
                                errorText="Required"
                            />
                        </div>
                    </div>
                )}
                <div
                    style={{
                        justifyContent: "flex-end",
                        width: "100%",
                        display: "flex",
                        flexWrap: "wrap",
                        boxSizing: "border-box",
                    }}
                >
                    <Button onClick={toggleOutsideSplitModal} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleSaveOutsideSplit(splitId, values)} variant="contained" color="primary">
                        Save
                    </Button>
                </div>
            </StyledModalPaper>
        </Modal>
    );
};

// We need an intermediary variable for handling the recursive nesting.
const OutsideSplitModalWrapped = OutsideSplitModal;

export default OutsideSplitModalWrapped;
